import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import { bindActionCreators } from 'redux'

import { Search, X, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react'

import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select'
import { Alert } from '@/components/ui/alert'
import { Spinner } from '@/components/ui/spinner'
import { DataTable } from '@/components/ui/data-table'

import {
  CLIENT_NAME_COLUMN,
  PROGRAM_NAME_COLUMN,
  FILE_NAME_COLUMN,
  AIRING_STATIONS_COLUMN,
  DELIVERY_TO_COLUMN,
  DELIVERY_DATE_COLUMN,
  CLIENT_NAME_COLUMN_FRIENDLY,
  PROGRAM_NAME_COLUMN_FRIENDLY,
  FILE_NAME_COLUMN_FRIENDLY,
  AIRING_STATIONS_COLUMN_FRIENDLY,
  DELIVERY_TO_COLUMN_FRIENDLY,
  DELIVERY_DATE_COLUMN_FRIENDLY,
  requestFiles,
  requestSortFiles,
  requestLoadfile,
  requestSearch,
  requestPageStep,
  requestDropdownFilters,
  distinctValues
} from '../../stores/files'

const fmtDateTime = val => {
  if (!val) return ''
  const d = new Date(val)
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString()}`
}

function ViewAll({
  files,
  totalSubsetFiles,
  subsetFiles,
  loading,
  error,
  hasError,
  sortColumn,
  sortDirection,
  searchTerm,
  clientFilter,
  programFilter,
  pageNumber,
  numberOfPages,
  requestFiles,
  requestSortFiles,
  requestLoadfile,
  requestSearch,
  requestPageStep,
  requestDropdownFilters
}) {
  useEffect(() => {
    if (files.length < 1) requestFiles()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Local input state so the field feels instant; Redux dispatch is debounced 250ms
  const [localSearch, setLocalSearch] = useState(searchTerm)

  const ALL = '__all__'
  const clientOptions = useMemo(() => distinctValues(files, CLIENT_NAME_COLUMN), [files])
  const programOptions = useMemo(
    () => distinctValues(files, PROGRAM_NAME_COLUMN, f => !clientFilter || f[CLIENT_NAME_COLUMN] === clientFilter),
    [files, clientFilter]
  )

  const onClientChange = value => {
    const client = value === ALL ? '' : value
    // keep the program only if it still exists under the new client
    const keepProgram = programFilter && files.some(f => f[PROGRAM_NAME_COLUMN] === programFilter && (!client || f[CLIENT_NAME_COLUMN] === client))
    requestDropdownFilters({clientFilter: client, programFilter: keepProgram ? programFilter : ''})
  }

  const onProgramChange = value => requestDropdownFilters({clientFilter, programFilter: value === ALL ? '' : value})
  const debounceTimer = useRef(null)

  const handleSearchChange = useCallback(value => {
    setLocalSearch(value)
    clearTimeout(debounceTimer.current)
    debounceTimer.current = setTimeout(() => requestSearch(value), 250)
  }, [requestSearch])

  const handleSearchClear = useCallback(() => {
    setLocalSearch('')
    clearTimeout(debounceTimer.current)
    requestSearch('')
  }, [requestSearch])

  useEffect(() => {
    setLocalSearch(searchTerm)
  }, [searchTerm])

  // Bridge Redux sort state <-> TanStack sorting (sorting is done in the store)
  const sorting = useMemo(
    () => (sortColumn ? [{ id: sortColumn, desc: (sortDirection[sortColumn] || 'asc') === 'desc' }] : []),
    [sortColumn, sortDirection]
  )
  const handleSortingChange = updater => {
    const next = typeof updater === 'function' ? updater(sorting) : updater
    if (!next.length) return
    requestSortFiles(next[0].id, next[0].desc ? 'desc' : 'asc')
  }

  const columns = useMemo(() => {
    const truncCell = ({ getValue }) => {
      const v = getValue() || ''
      return <span title={v}>{v}</span>
    }
    return [
      {
        id: '#',
        header: '#',
        accessorKey: '#',
        size: 38,
        enableSorting: false,
        enableResizing: false,
        meta: { reorderable: false, align: 'right', cellClassName: 'text-muted-foreground' },
      },
      {
        id: FILE_NAME_COLUMN,
        header: FILE_NAME_COLUMN_FRIENDLY,
        accessorKey: FILE_NAME_COLUMN,
        size: 320,
        cell: ({ row }) => (
          <button
            type="button"
            onClick={() => requestLoadfile(row.original.fileId)}
            title={row.original[FILE_NAME_COLUMN]}
            className="max-w-full truncate font-medium text-primary hover:underline"
          >
            {row.original[FILE_NAME_COLUMN]}
          </button>
        ),
      },
      {
        id: CLIENT_NAME_COLUMN,
        header: CLIENT_NAME_COLUMN_FRIENDLY,
        accessorKey: CLIENT_NAME_COLUMN,
        cell: truncCell,
        size: 160,
      },
      {
        id: PROGRAM_NAME_COLUMN,
        header: PROGRAM_NAME_COLUMN_FRIENDLY,
        accessorKey: PROGRAM_NAME_COLUMN,
        cell: truncCell,
        size: 180,
      },
      {
        id: AIRING_STATIONS_COLUMN,
        header: AIRING_STATIONS_COLUMN_FRIENDLY,
        accessorKey: AIRING_STATIONS_COLUMN,
        cell: truncCell,
        size: 150,
      },
      {
        id: DELIVERY_TO_COLUMN,
        header: DELIVERY_TO_COLUMN_FRIENDLY,
        accessorKey: DELIVERY_TO_COLUMN,
        cell: truncCell,
        size: 150,
      },
      {
        id: DELIVERY_DATE_COLUMN,
        header: DELIVERY_DATE_COLUMN_FRIENDLY,
        accessorKey: DELIVERY_DATE_COLUMN,
        size: 170,
        cell: ({ getValue }) => fmtDateTime(getValue()),
      },
    ]
  }, [requestLoadfile])

  return (
    <div className="space-y-3">
      {hasError && <Alert variant="error">{error}</Alert>}

      <Card className="overflow-hidden">
        <div className="flex flex-wrap items-center gap-3 border-b px-3 py-2">
          <div className="min-w-0 flex-1">
            <h2 className="text-sm font-semibold leading-tight">Delivered Files</h2>
            <p className="text-xs text-muted-foreground">
              {`${totalSubsetFiles} total · ${subsetFiles.length} shown · Page ${pageNumber}/${Math.max(numberOfPages, 1)}`}
              {clientFilter ? ` · ${clientFilter}` : ''}
              {programFilter ? ` · ${programFilter}` : ''}
              {searchTerm ? ` · "${searchTerm}"` : ''}
            </p>
          </div>

          <Select value={clientFilter || ALL} onValueChange={onClientChange}>
            <SelectTrigger className="h-8 w-48 text-xs" aria-label="Filter by client">
              <SelectValue placeholder="All clients" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All clients</SelectItem>
              {clientOptions.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>

          <Select value={programFilter || ALL} onValueChange={onProgramChange}>
            <SelectTrigger className="h-8 w-52 text-xs" aria-label="Filter by program">
              <SelectValue placeholder="All programs" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All programs</SelectItem>
              {programOptions.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
            </SelectContent>
          </Select>

          <div className="relative w-56">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search client, program, file, station, date…"
              value={localSearch}
              onChange={e => handleSearchChange(e.target.value)}
              onKeyDown={e => { if (e.key === 'Escape') handleSearchClear() }}
              className="h-8 pl-8 pr-8"
            />
            {localSearch.length > 0 && (
              <button
                onClick={handleSearchClear}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                aria-label="Clear search"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          <Button variant="outline" size="icon-sm" onClick={requestFiles} disabled={loading} title="Refresh list">
            {loading ? <Spinner size={16} /> : <RefreshCw className="h-4 w-4" />}
          </Button>

          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="icon-sm"
              onClick={() => requestPageStep(pageNumber - 1)}
              disabled={pageNumber <= 1}
              title="Previous page"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Input
              type="number"
              value={numberOfPages > 0 ? pageNumber : 1}
              onChange={e => requestPageStep(parseInt(e.target.value) || 1)}
              onFocus={e => e.target.select()}
              min={1}
              max={Math.max(numberOfPages, 1)}
              className="h-8 w-14 text-center"
            />
            <span className="whitespace-nowrap text-sm text-muted-foreground">
              / {Math.max(numberOfPages, 1)}
            </span>
            <Button
              variant="outline"
              size="icon-sm"
              onClick={() => requestPageStep(pageNumber + 1)}
              disabled={pageNumber >= numberOfPages}
              title="Next page"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <DataTable
          tableId="deliverables-v2"
          columns={columns}
          data={subsetFiles}
          sorting={sorting}
          onSortingChange={handleSortingChange}
          manualSorting
          enableSortingRemoval={false}
          getRowId={(row, i) => row.id || String(i)}
          isLoading={loading}
          emptyMessage="Nothing to show"
        />

        <div className="flex items-center justify-center gap-2 border-t px-3 py-2">
          <Button variant="outline" size="sm" onClick={() => requestPageStep(pageNumber - 1)} disabled={pageNumber <= 1}>
            <ChevronLeft className="h-4 w-4" /> Previous
          </Button>
          <Button variant="outline" size="sm" onClick={() => requestPageStep(pageNumber + 1)} disabled={pageNumber >= numberOfPages}>
            Next <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </Card>
    </div>
  )
}

const mapStateToProps = (state, route) => {
  const {
    files,
    totalSubsetFiles,
    subsetFiles,
    loading,
    error,
    hasError,
    pageNumber,
    pageSize,
    numberOfPages,
    sortColumn,
    sortDirection,
    searchTerm,
    clientFilter,
    programFilter
  } = state.stores.files

  return {
    ...route.match.params,
    files,
    totalSubsetFiles,
    subsetFiles,
    loading,
    error,
    hasError,
    pageNumber,
    pageSize,
    numberOfPages,
    sortColumn,
    sortDirection,
    searchTerm,
    clientFilter,
    programFilter
  }
}

const mapDispatchToProps = dispatch => ({
  ...bindActionCreators({
    requestFiles,
    requestSortFiles,
    requestLoadfile,
    requestSearch,
    requestPageStep,
    requestDropdownFilters
  }, dispatch)
})

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(ViewAll))
